import { format, isValid, parseISO } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';

// Common timezone list for the dropdown
export const TIMEZONE_LIST = [
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Toronto', label: 'Toronto (ET)' },
  { value: 'America/Vancouver', label: 'Vancouver (PT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Europe/Madrid', label: 'Madrid (CET/CEST)' },
  { value: 'Europe/Rome', label: 'Rome (CET/CEST)' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam (CET/CEST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Kolkata', label: 'Mumbai/Kolkata (IST)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEDT/AEST)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEDT/AEST)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZDT/NZST)' }
];

/**
 * Get the user's current timezone
 */
export const getCurrentTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Format a date value with timezone support
 * @param value - The date value to format
 * @param timezone - The timezone to display the date in
 * @param includeTime - Whether to include time in the output
 * @returns Formatted date string with timezone info
 */
export const formatDateWithTimezone = (
  value: any, 
  timezone: string = getCurrentTimezone(), 
  includeTime: boolean = false,
  includeTimezone: boolean = false
): string => {
  if (!value) return '-';

  try {
    let date: Date | null = null;

    // Handle different date input types
    if (value instanceof Date) {
      date = value;
    } else if (typeof value === 'string') {
      date = new Date(value);
    } else if (typeof value === 'number') {
      date = new Date(value);
    }

    // Validate the parsed date
    if (date && isValid(date)) {
      if (includeTime) {
        return formatInTimeZone(date, timezone, 'dd MMM yyyy, hh:mm a zzz');
      } 

      if (includeTimezone) {
        return formatInTimeZone(date, timezone, 'dd MMM yyyy, zzz');
      }

      return formatInTimeZone(date, timezone, 'dd MMM yyyy');
    }
  } catch (error) {
    console.warn('Date parsing error:', error);
  }

  return typeof value === 'string' ? value : String(value);
};

/**
 * Create a date object with timezone information without any conversions
 * @param dateValue - The date value
 * @param timezone - The timezone
 * @returns Object containing date and timezone exactly as selected
 */
export const createDateWithTimezone = (dateValue: Date | string | null, timezone: string, includeTime: boolean = false, includeTimezone: boolean = false) => {
  if (!dateValue) return null;
  
  let date: Date;
  if (dateValue instanceof Date) {
    date = dateValue;
  } else {
    date = new Date(dateValue);
  }
  
  // Store date with or without time based on includeTime parameter
  let dateString: string;
  if (includeTime) {
    // Store full datetime in ISO format
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    dateString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  } else {
    // Store date only in YYYY-MM-DD format
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    dateString = `${year}-${month}-${day}`;
  }
  
  return {
    date: dateString,
    timezone: timezone,
    timestamp: date.getTime(),
    includeTime: includeTime,
    includeTimezone: includeTimezone
  };
};

/**
 * Parse a date object with timezone information
 * @param dateObj - Object containing date and timezone info
 * @returns Parsed date in the specified timezone
 */
export const parseDateWithTimezone = (dateObj: any) => {
  if (!dateObj) return null;
  
  // Handle legacy format (just date string)
  if (typeof dateObj === 'string' || dateObj instanceof Date) {
    return new Date(dateObj);
  }
  
  // Handle new format with timezone
  if (dateObj.date && dateObj.timezone) {
    const utcDate = new Date(dateObj.date);
    return toZonedTime(utcDate, dateObj.timezone);
  }
  
  return null;
};

/**
 * Format a date value to a readable format with timezone support
 * @param value - The date value to format (string, number, Date, or timezone object)
 * @param includeTime - Whether to include time in the output
 * @returns Formatted date string or the original value if not a valid date
 */
export const formatDateValue = (value: any, includeTime: boolean = false): string => {
  if (!value) {
    return '-';
  }

  try {
    let date: Date | null = null;
    let timezone: string | null = null;

    // Handle new format with timezone information
    if (typeof value === 'object' && value !== null && !Array.isArray(value) && 'date' in value && 'timezone' in value) {
      timezone = value.timezone;
      const shouldIncludeTimezone = value.includeTimezone === true; // Only show timezone if explicitly set to true
      
      // Handle both date-only format (YYYY-MM-DD) and full ISO format
      if (value.date.includes('T')) {
        // Full ISO format with time
        date = parseISO(value.date);
      } else {
        // Date-only format (YYYY-MM-DD) - parse as local date to avoid timezone shifts
        const [year, month, day] = value.date.split('-').map(Number);
        date = new Date(year, month - 1, day); // month is 0-based in Date constructor
      }
      
      if (date && isValid(date) && timezone) {
        if (!value.date.includes('T')) {
          // For date-only format, don't apply timezone conversion to avoid date shifts
          const shouldShowTime = includeTime || value.includeTime;
          if (shouldShowTime && shouldIncludeTimezone) {
            return `${format(date, 'dd MMM yyyy')}, 12:00 AM ${formatInTimeZone(new Date(), timezone, 'zzz')}`;
          } else {
            return format(date, 'dd MMM yyyy');
          }
        } else {
          // For full datetime format, show the exact time as stored with timezone
          const shouldShowTime = includeTime || value.includeTime;
          if (shouldShowTime) {
            // Format exactly as requested: "YYYY-MM-DD, HH:MM AM/PM" with timezone
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = date.getHours();
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const displayHours = hours % 12 || 12; // Convert 0 to 12 for 12AM
            
            if (shouldIncludeTimezone) {
              // Get timezone info
              const timezoneInfo = TIMEZONE_LIST.find(tz => tz.value === timezone);
              const fullTimezoneName = timezoneInfo ? timezoneInfo.label : timezone;
              
              // Get timezone abbreviation
              let timezoneAbbr;
              try {
                timezoneAbbr = formatInTimeZone(date, timezone, 'zzz');
                
                if (timezoneAbbr.startsWith('GMT')) {
                  const intlFormat = new Intl.DateTimeFormat('en-US', {
                    timeZone: timezone,
                    timeZoneName: 'short'
                  });
                  const parts = intlFormat.formatToParts(date);
                  const tzPart = parts.find(part => part.type === 'timeZoneName');
                  if (tzPart && !tzPart.value.startsWith('GMT')) {
                    timezoneAbbr = tzPart.value;
                  }
                }
              } catch (error) {
                timezoneAbbr = formatInTimeZone(date, timezone, 'zz');
              }
              
              return `${year}-${month}-${day}, ${String(displayHours).padStart(2, '0')}:${minutes} ${ampm} (${fullTimezoneName} - ${timezoneAbbr})`;
            } else {
              // Without timezone info
              return `${year}-${month}-${day}, ${String(displayHours).padStart(2, '0')}:${minutes} ${ampm}`;
            }
          } else {
            return formatInTimeZone(date, timezone, 'dd MMM yyyy');
          }
        }
      }
    }
    
    // Handle legacy date input types
    else if (value instanceof Date) {
      date = value;
    } else if (typeof value === 'string') {
      // Check if it's an ISO string format
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?$/.test(value)) {
        date = parseISO(value);
      } else {
        // Try to parse other string formats
        date = new Date(value);
      }
    } else if (typeof value === 'number') {
      // Handle timestamps
      date = new Date(value);
    }

    // Validate and format the parsed date (for legacy formats)
    if (date && isValid(date)) {
      const result = includeTime 
        ? format(date, 'dd MMM yyyy, hh:mm a')
        : format(date, 'dd MMM yyyy');
      return result;
    }
  } catch (error) {
    console.warn('formatDateValue error:', error, 'for value:', value);
  }

  // Return string representation if it's not a valid date
  return typeof value === 'string' ? value : String(value);
};

/**
 * Format a date value to a short format for tables
 * @param value - The date value to format
 * @returns Short formatted date string
 */
export const formatDateShort = (value: any): string => {
  if (!value) return '-';

  try {
    let date: Date | null = null;

    // Handle new format with timezone information
    if (typeof value === 'object' && value !== null && 'date' in value && 'timezone' in value) {
      const timezone = value.timezone;
      
      // Handle both date-only format (YYYY-MM-DD) and full ISO format
      if (value.date.includes('T')) {
        // Full ISO format with time - parse and show datetime with timezone abbreviation
        const dateTimeStr = value.date; // e.g., "2025-12-31T08:00:00"
        const [datePart, timePart] = dateTimeStr.split('T');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, minutes] = timePart.split(':').map(Number);
        
        date = new Date(year, month - 1, day, hours, minutes);
        
        if (date && isValid(date)) {
          // Format: "31/12/25 08:00 AM (TZ)"
          const formattedDate = format(date, 'dd/MM/yy');
          const formattedTime = format(date, 'hh:mm a');
          
          // Get timezone abbreviation
          let timezoneAbbr;
          try {
            timezoneAbbr = formatInTimeZone(date, timezone, 'zzz');
            
            if (timezoneAbbr.startsWith('GMT')) {
              const intlFormat = new Intl.DateTimeFormat('en-US', {
                timeZone: timezone,
                timeZoneName: 'short'
              });
              const parts = intlFormat.formatToParts(date);
              const tzPart = parts.find(part => part.type === 'timeZoneName');
              if (tzPart && !tzPart.value.startsWith('GMT')) {
                timezoneAbbr = tzPart.value;
              }
            }
          } catch (error) {
            timezoneAbbr = formatInTimeZone(date, timezone, 'zz');
          }
          
          return `${formattedDate} ${formattedTime} (${timezoneAbbr})`;
        }
      } else {
        // Date-only format (YYYY-MM-DD) - parse as local date to avoid timezone shifts
        const [year, month, day] = value.date.split('-').map(Number);
        date = new Date(year, month - 1, day);
        if (date && isValid(date)) {
          return format(date, 'dd/MM/yy');
        }
      }
    }
    // Handle legacy formats (plain strings)
    else if (typeof value === 'string') {
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?$/.test(value)) {
        // For ISO string with time, extract just the date part (no timezone info available)
        const dateStr = value.split('T')[0]; // Get "YYYY-MM-DD" part
        const [year, month, day] = dateStr.split('-').map(Number);
        date = new Date(year, month - 1, day);
      } else {
        date = new Date(value);
      }
    }
    // Handle Date objects
    else if (value instanceof Date) {
      date = value;
    }
    // Handle timestamps
    else if (typeof value === 'number') {
      date = new Date(value);
    }

    if (date && isValid(date)) {
      return format(date, 'dd/MM/yy');
    }
  } catch (error) {
    console.warn('Date parsing error:', error);
  }

  return typeof value === 'string' ? value : String(value);
};

/**
 * Check if a value is a valid date
 * @param value - The value to check
 * @returns True if the value is a valid date
 */
export const isValidDateValue = (value: any): boolean => {
  if (!value) return false;

  try {
    let date: Date | null = null;

    // Check for new format with timezone
    if (typeof value === 'object' && value !== null && !Array.isArray(value) && 'date' in value) {
      // This is the new timezone format
      if (typeof value.date === 'string') {
        if (value.date.includes('T')) {
          date = parseISO(value.date);
        } else {
          // Date-only format YYYY-MM-DD
          const [year, month, day] = value.date.split('-').map(Number);
          date = new Date(year, month - 1, day);
        }
      }
      return date ? isValid(date) : false;
    }
    
    if (value instanceof Date) {
      date = value;
    } else if (typeof value === 'string') {
      // Only check for proper date formats, not all strings
      const datePatterns = [
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?$/, // ISO format
        /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
        /^\d{2}\/\d{2}\/\d{4}$/, // MM/DD/YYYY or DD/MM/YYYY
        /^\d{2}-\d{2}-\d{4}$/, // MM-DD-YYYY or DD-MM-YYYY
        /^\d{1,2}\/\d{1,2}\/\d{4}$/, // M/D/YYYY or D/M/YYYY
        /^\d{1,2}-\d{1,2}-\d{4}$/, // M-D-YYYY or D-M-YYYY
      ];
      
      // First check if it matches a known date pattern
      const matchesPattern = datePatterns.some(pattern => pattern.test(value));
      if (!matchesPattern) {
        return false;
      }
      
      // Then try to parse it
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?$/.test(value)) {
        date = parseISO(value);
      } else {
        date = new Date(value);
      }
    } else if (typeof value === 'number') {
      // A bare number is NEVER a date here. Every date this app stores is
      // either a {date, timezone} object or an ISO string — a plain number is
      // field data (a quantity, a count, a reference number). Reading it as an
      // epoch-millisecond timestamp turned every small number into
      // "01 Jan 1970" (new Date(2) is 2ms past the epoch), which made a
      // NUMBER field's value unreadable AND identical for every row.
      return false;
    }

    return date ? isValid(date) : false;
  } catch {
    return false;
  }
};

/**
 * Format date for display in lists and cards
 * @param value - The date value to format
 * @returns Formatted date string suitable for display
 */
export const formatDisplayDate = (value: any): string => {
  if (!value) return '-';

  try {
    let date: Date | null = null;

    // Handle new format with timezone information
    if (typeof value === 'object' && value !== null && 'date' in value && 'timezone' in value) {
      const timezone = value.timezone;
      
      // Handle both date-only format (YYYY-MM-DD) and full ISO format
      if (value.date.includes('T')) {
        // Full ISO format with time - parse and show datetime with timezone
        const dateTimeStr = value.date; // e.g., "2025-12-31T08:00:00"
        const [datePart, timePart] = dateTimeStr.split('T');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, minutes] = timePart.split(':').map(Number);
        
        date = new Date(year, month - 1, day, hours, minutes);
        
        if (date && isValid(date)) {
          // Format: "Dec 31, 2025, 08:00 AM (Timezone)"
          const formattedDate = format(date, 'MMM dd, yyyy');
          const formattedTime = format(date, 'hh:mm a');
          
          // Get timezone abbreviation
          let timezoneAbbr;
          try {
            timezoneAbbr = formatInTimeZone(date, timezone, 'zzz');
            
            if (timezoneAbbr.startsWith('GMT')) {
              const intlFormat = new Intl.DateTimeFormat('en-US', {
                timeZone: timezone,
                timeZoneName: 'short'
              });
              const parts = intlFormat.formatToParts(date);
              const tzPart = parts.find(part => part.type === 'timeZoneName');
              if (tzPart && !tzPart.value.startsWith('GMT')) {
                timezoneAbbr = tzPart.value;
              }
            }
          } catch (error) {
            timezoneAbbr = formatInTimeZone(date, timezone, 'zz');
          }
          
          return `${formattedDate}, ${formattedTime} (${timezoneAbbr})`;
        }
      } else {
        // Date-only format (YYYY-MM-DD) - parse as local date to avoid timezone shifts
        const [year, month, day] = value.date.split('-').map(Number);
        date = new Date(year, month - 1, day);
        if (date && isValid(date)) {
          return format(date, 'MMM dd, yyyy');
        }
      }
    }
    // Handle legacy formats (plain strings)
    else if (typeof value === 'string') {
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?$/.test(value)) {
        // For ISO string with time, extract just the date part (no timezone info available)
        const dateStr = value.split('T')[0]; // Get "YYYY-MM-DD" part
        const [year, month, day] = dateStr.split('-').map(Number);
        date = new Date(year, month - 1, day);
      } else {
        date = new Date(value);
      }
    } 
    // Handle Date objects
    else if (value instanceof Date) {
      date = value;
    } 
    // Handle timestamps
    else if (typeof value === 'number') {
      date = new Date(value);
    }

    if (date && isValid(date)) {
      return format(date, 'MMM dd, yyyy');
    }
  } catch (error) {
    console.warn('Date parsing error:', error);
  }

  // If we couldn't parse it and it's an object, try to extract meaningful info
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    if ('date' in value) {
      return value.date.split('T')[0]; // Return just the date part
    }
    return '-'; // Don't show [object Object]
  }
  
  return typeof value === 'string' ? value : String(value);
};

/**
 * Format date for display with timezone information but without time
 * @param value - The date value to format
 * @returns Formatted date string with timezone but no time
 */
export const formatDateWithTimezoneDisplay = (value: any): string => {
  if (!value) return '-';

  try {
    let date: Date | null = null;
    let timezone: string | null = null;

    // Handle new format with timezone information
    if (typeof value === 'object' && value !== null && !Array.isArray(value) && 'date' in value && 'timezone' in value) {
      timezone = value.timezone;
      const shouldIncludeTimezone = value.includeTimezone === true; // Only show timezone if explicitly set to true
      
      // Handle both date-only format (YYYY-MM-DD) and full ISO format
      if (value.date.includes('T')) {
        // Full ISO format with time
        date = parseISO(value.date);
      } else {
        // Date-only format (YYYY-MM-DD) - parse as local date to avoid timezone shifts
        const [year, month, day] = value.date.split('-').map(Number);
        date = new Date(year, month - 1, day); // month is 0-based in Date constructor
      }
      
      if (date && isValid(date)) {
        // Check if we should include time in display
        const shouldIncludeTime = value.includeTime || value.date.includes('T');
        
        // Handle display based on whether time is included
        if (!shouldIncludeTime) {
          // Format as local date to avoid timezone conversion
          const dateStr = format(date, 'dd MMM yyyy');
          
          if (shouldIncludeTimezone && timezone) {
            // Get the full timezone name from the TIMEZONE_LIST
            const timezoneInfo = TIMEZONE_LIST.find(tz => tz.value === timezone);
            const fullTimezoneName = timezoneInfo ? timezoneInfo.label : timezone;
            
            // Use the actual date to get the correct timezone abbreviation
            let timezoneAbbr;
            
            try {
              const targetDate = new Date(date);
              targetDate.setHours(12);
              timezoneAbbr = formatInTimeZone(targetDate, timezone, 'zzz');
              
              if (timezoneAbbr.startsWith('GMT')) {
                const intlFormat = new Intl.DateTimeFormat('en-US', {
                  timeZone: timezone,
                  timeZoneName: 'short'
                });
                const parts = intlFormat.formatToParts(targetDate);
                const tzPart = parts.find(part => part.type === 'timeZoneName');
                if (tzPart && !tzPart.value.startsWith('GMT')) {
                  timezoneAbbr = tzPart.value;
                }
              }
            } catch (error) {
              timezoneAbbr = formatInTimeZone(date, timezone, 'zz');
            }
            
            return `${dateStr} (${fullTimezoneName} - ${timezoneAbbr})`;
          } else {
            // No timezone info
            return dateStr;
          }
        } else {
          // For datetime format, show the exact time as stored
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = date.getHours();
          const minutes = String(date.getMinutes()).padStart(2, '0');
          const ampm = hours >= 12 ? 'PM' : 'AM';
          const displayHours = hours % 12 || 12;
          
          if (shouldIncludeTimezone && timezone) {
            // Get the full timezone name from the TIMEZONE_LIST
            const timezoneInfo = TIMEZONE_LIST.find(tz => tz.value === timezone);
            const fullTimezoneName = timezoneInfo ? timezoneInfo.label : timezone;
            
            // Get timezone abbreviation for display
            let timezoneAbbr;
            try {
              timezoneAbbr = formatInTimeZone(date, timezone, 'zzz');
              
              if (timezoneAbbr.startsWith('GMT')) {
                const intlFormat = new Intl.DateTimeFormat('en-US', {
                  timeZone: timezone,
                  timeZoneName: 'short'
                });
                const parts = intlFormat.formatToParts(date);
                const tzPart = parts.find(part => part.type === 'timeZoneName');
                if (tzPart && !tzPart.value.startsWith('GMT')) {
                  timezoneAbbr = tzPart.value;
                }
              }
            } catch (error) {
              timezoneAbbr = formatInTimeZone(date, timezone, 'zz');
            }
            
            return `${year}-${month}-${day}, ${String(displayHours).padStart(2, '0')}:${minutes} ${ampm} (${fullTimezoneName} - ${timezoneAbbr})`;
          } else {
            // No timezone info
            return `${year}-${month}-${day}, ${String(displayHours).padStart(2, '0')}:${minutes} ${ampm}`;
          }
        }
      }
    }
    
    // Handle legacy date input types - no timezone info available
    else if (value instanceof Date) {
      date = value;
    } else if (typeof value === 'string') {
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?$/.test(value)) {
        date = parseISO(value);
      } else {
        date = new Date(value);
      }
    } else if (typeof value === 'number') {
      date = new Date(value);
    }

    // For legacy formats, just show the date without timezone
    if (date && isValid(date)) {
      return format(date, 'dd MMM yyyy');
    }
  } catch (error) {
    console.warn('formatDateWithTimezoneDisplay error:', error, 'for value:', value);
  }

  return typeof value === 'string' ? value : String(value);
};

/**
 * Format a single value that may have been sourced from a DATE or RESOURCE
 * field. Dynamic fields (SELECT/RADIO/CHECKBOX) carry such objects while
 * reporting their own inputComponent, so callers cannot switch on the type —
 * detect by SHAPE. Keeps an object from rendering as "[object Object]" (or, in
 * a raw JSX child, throwing "Objects are not valid as a React child").
 */
export const formatDynamicScalar = (val: any): string => {
  if (isValidDateValue(val)) return formatDateWithTimezoneDisplay(val);
  if (val && typeof val === 'object') {
    const fields = (val as any).fields;
    if (fields && typeof fields === 'object') {
      const scalars = Object.values(fields)
        .filter((v) => v !== null && v !== undefined && v !== '' && typeof v !== 'object')
        .slice(0, 2)
        .map(String);
      if (scalars.length) return scalars.join(' ');
    }
    const id = (val as any).id ?? (val as any)._id;
    if (id != null) return String(id);
    return JSON.stringify(val);
  }
  return String(val ?? '');
};

// Browsers return GMT offsets for ambiguous abbreviations (IST = India/Israel/Ireland, CST = China/Central US, etc.)
const TIMEZONE_ABBR_OVERRIDE: Record<string, string> = {
  'Asia/Kolkata':        'IST',
  'Asia/Calcutta':       'IST',
  'Asia/Dubai':          'GST',
  'Asia/Singapore':      'SGT',
  'Asia/Hong_Kong':      'HKT',
  'Asia/Tokyo':          'JST',
  'Asia/Shanghai':       'CST',
  'Asia/Karachi':        'PKT',
  'Asia/Dhaka':          'BST',
  'Asia/Colombo':        'SLST',
  'Asia/Kathmandu':      'NPT',
  'Asia/Yangon':         'MMT',
  'Asia/Bangkok':        'ICT',
  'Asia/Jakarta':        'WIB',
};

// Get proper timezone abbreviation (e.g., "IST", "PST", "EDT")
export const getTimezoneAbbreviation = (timezone: string, date: Date = new Date()): string => {
  // Use manual override first for timezones browsers report as GMT offsets
  if (TIMEZONE_ABBR_OVERRIDE[timezone]) {
    return TIMEZONE_ABBR_OVERRIDE[timezone];
  }

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short'
    });

    const parts = formatter.formatToParts(date);
    const timeZonePart = parts.find(part => part.type === 'timeZoneName');
    const abbr = timeZonePart ? timeZonePart.value : timezone;

    // If browser still returns a GMT offset (e.g. "GMT+5:30"), fall back to the raw timezone name
    return abbr.startsWith('GMT+') || abbr.startsWith('GMT-') ? timezone : abbr;
  } catch (error) {
    console.error('Error getting timezone abbreviation:', error);
    return timezone;
  }
};

// Get timezone offset in minutes
export const getTimezoneOffset = (timezone: string, date: Date = new Date()): number => {
  try {
    // Create dates in UTC and target timezone
    const utc = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const target = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    
    // Calculate offset in minutes
    return (utc.getTime() - target.getTime()) / (1000 * 60);
  } catch (error) {
    console.error('Error getting timezone offset:', error);
    return 0;
  }
};

// Format timezone with proper abbreviation and offset
export const formatTimezoneDisplay = (timezone: string, date: Date = new Date()): string => {
  const abbreviation = getTimezoneAbbreviation(timezone, date);
  const offset = getTimezoneOffset(timezone, date);
  const offsetHours = Math.floor(Math.abs(offset) / 60);
  const offsetMinutes = Math.abs(offset) % 60;
  const sign = offset <= 0 ? '+' : '-';
  
  const offsetString = `${sign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`;
  
  return `${abbreviation} (GMT${offsetString})`;
};

/**
 * Format a timestamp using the user's local timezone
 * Shows date, time, and timezone name/offset
 * @param value - The timestamp value to format (string, number, or Date)
 * @returns Formatted string with date, time, and timezone info
 */
export const formatTimestampWithLocalTimezone = (value: any): string => {
  if (!value) return '-';

  try {
    let date: Date | null = null;
    let shouldIncludeTimezone = true; // Default to true for legacy values

    // Handle different input types
    if (value instanceof Date) {
      date = value;
    } else if (typeof value === 'string') {
      // Try parsing ISO format or other string formats
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?$/.test(value)) {
        date = parseISO(value);
      } else {
        date = new Date(value);
      }
    } else if (typeof value === 'number') {
      // Handle Unix timestamps
      date = new Date(value);
    } else if (typeof value === 'object' && value !== null && 'date' in value) {
      // Handle timezone-aware date objects
      shouldIncludeTimezone = value.includeTimezone === true; // Only show timezone if explicitly set to true
      if (value.date.includes('T')) {
        date = parseISO(value.date);
      } else {
        const [year, month, day] = value.date.split('-').map(Number);
        date = new Date(year, month - 1, day);
      }
    }

    // Validate the date
    if (date && isValid(date)) {
      // Get user's local timezone
      const userTimezone = getCurrentTimezone();
      
      // Format the date and time in the user's timezone
      const formattedDate = formatInTimeZone(date, userTimezone, 'dd MMM yyyy');
      const formattedTime = formatInTimeZone(date, userTimezone, 'hh:mm a');
      
      if (shouldIncludeTimezone) {
        // Return formatted string: "DD MMM YYYY, HH:MM AM/PM (IST)"
        const tzAbbr = getTimezoneAbbreviation(userTimezone, date);
        return `${formattedDate}, ${formattedTime} (${tzAbbr})`;
      } else {
        // Return formatted string without timezone
        return `${formattedDate}, ${formattedTime}`;
      }
    }
  } catch (error) {
    console.warn('formatTimestampWithLocalTimezone error:', error, 'for value:', value);
  }

  // Fallback to string representation
  return typeof value === 'string' ? value : String(value);
};

// Returns the full human-readable timezone name, e.g. "India Standard Time"
export const getTimezoneFullName = (timezone: string, date: Date = new Date()): string => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'long'
    });
    const parts = formatter.formatToParts(date);
    const tzPart = parts.find(p => p.type === 'timeZoneName');
    return tzPart ? tzPart.value : timezone;
  } catch {
    return timezone;
  }
};
